import { gql } from '@apollo/client'

/**
 * What a storefront *offers* for one checkout rule — the calendar days it can
 * deliver on, or the payment methods it accepts.
 *
 * The third of the three things called `checkoutRules`, and the only one this
 * document touches. It reads the `CheckoutRules` collection, not
 * `configuration.checkoutRules` (the cart validation map) and not
 * `session.checkoutRules` (what the shopper already picked). Mixing the first
 * two disables a storefront's rule enforcement outright — COM-736.
 *
 * ⚠️ **`data` is a leaf and must stay one.** The schema types it `[JSON]`, so
 * `data { ... }` is a `FieldsOnCorrectType` validation error: the server
 * returns no `data` for the query at all, rather than a partial result. The
 * same mistake against `session.checkoutRules` would have broken the whole
 * boot path, which is why `getSession/types.ts` carries the long version of
 * this warning and why `tests/query.test.ts` asserts the shape against the
 * parsed AST rather than the query string.
 *
 * ⚠️ **`ruleType` is `String!` on the wire, and `sessionUid` is `ID!`.** The
 * schema names no enum for either, so a typo in `ruleType` reaches the server
 * and comes back as an error. `IGetCheckoutRuleVariables` types it as
 * `CheckoutRules` to close that on this side. Note the mutation's own
 * `sessionUid` is `String!` — the two operations disagree, and each
 * declaration matches its own field.
 *
 * **Both failure and emptiness are now reachable, and they mean different
 * things.** Before COM-1066 a storefront with nothing to offer always arrived
 * on the error path: `getRule` threw `ERROR.NOT_FOUND`, `pushError` preserved
 * the code, and the response carried `extensions.code === '404'` with Apollo's
 * default `errorPolicy: 'none'` discarding `data`. COM-1076 (shared lookup)
 * narrows that: a rule the storefront *authored* but with no deliverable
 * options now arrives as `data: []` on the success path, and only a **rule
 * type the storefront does not serve** (a typo'd `DELIVERY_DATE` singular,
 * or a camelCase validation rule reaching an undefined handler) still 404s.
 * Classify the two separately — a 404 is a storefront configuration gap,
 * `data: []` is a shopper eligibility gap (no delivery window matches the
 * route, no payment method matches the customer), and the shopper message
 * differs. `useCheckoutRuleAutoSettle` reads both: the empty array as
 * `advance('empty')`, the error path (404 included) as
 * `advance('unavailable')`. `'failed'` is reserved for a rejected *write*
 * and the deadline interrupting one, so do not describe the 404 branch as
 * `'failed'` — the hook's own code has that distinction.
 * @internal
 */
const getCheckoutRuleQuery = gql`
  query GetCheckoutRule(
    $storefrontName: String!
    $ruleType: String!
    $sessionUid: ID!
  ) {
    getCheckoutRule(
      storefrontName: $storefrontName
      ruleType: $ruleType
      sessionUid: $sessionUid
    ) {
      ruleType
      data
      preselected
    }
  }
`

export default getCheckoutRuleQuery
